// ts-rest contract for the data-retention admin surface (ADR-0012).
//
// The View → Administration retention panel and any ops tooling drive the
// retention model through these five endpoints: read the schedule + governance
// state, DRY-RUN the sweep (report what would be deleted without deleting),
// record a per-category enablement decision (the compliance-ratification
// record for regulated categories), and place / release legal holds (the
// deletion-suspension register consulted before every delete).
//
// All endpoints are admin-persona-gated at the controller (ADR-0004). The
// retention category vocabulary is re-expressed here as a wire enum — the
// projection layer never imports the ontology runtime (ADR-0003 / PRD #159);
// the ontology-side source of truth is `RetentionCategory` in
// `@septima/ontology-core` and the schedule in
// `apps/server/src/lib/retention/retention-schedule.ts`.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

export const retentionCategorySchema = z.enum([
    'kyc_screening',
    'suitability_records',
    'call_records',
    'client_records',
    'vulnerability_assessments',
    'reconciliation_records',
    'agent_execution',
    'agent_artefacts',
    'audit_ledger',
    'activity_stream',
    'trace_diagnostics',
    'portal_access',
    'inbound_files',
]);
export type RetentionCategoryDto = z.infer<typeof retentionCategorySchema>;

/** One schedule row + its current governance state. */
export const retentionScheduleEntrySchema = z.object({
    category: retentionCategorySchema,
    periodLabel: z.string(),
    trigger: z.string(),
    basis: z.string(),
    /** Regulated categories default OFF and need a ratified enablement row. */
    regulated: z.boolean(),
    defaultEnabled: z.boolean(),
    /** The EFFECTIVE enablement (state row when present, else the default). */
    enabled: z.boolean(),
    notes: z.string().nullable(),
    /** Present when a `retention_policy_state` row exists for the category. */
    stateNote: z.string().nullable(),
    stateUpdatedBy: z.string().nullable(),
    stateUpdatedAt: z.string().nullable(),
});
export type RetentionScheduleEntryDto = z.infer<typeof retentionScheduleEntrySchema>;

export const legalHoldSchema = z.object({
    id: z.string(),
    subjectKind: z.enum(['firm', 'client', 'user']),
    subjectId: z.string().nullable(),
    reason: z.string(),
    reference: z.string().nullable(),
    createdBy: z.string().nullable(),
    createdAt: z.string(),
    releasedAt: z.string().nullable(),
    releasedBy: z.string().nullable(),
    releaseNote: z.string().nullable(),
});
export type LegalHoldDto = z.infer<typeof legalHoldSchema>;

export const retentionCategoryReportSchema = z.object({
    category: retentionCategorySchema,
    periodLabel: z.string(),
    trigger: z.string(),
    regulated: z.boolean(),
    enabled: z.boolean(),
    /** Null = the category has no selectable anchor yet (see notes). */
    candidates: z.number().int().nullable(),
    deleted: z.number().int(),
    heldBack: z.number().int(),
    notes: z.array(z.string()),
});
export type RetentionCategoryReportDto = z.infer<typeof retentionCategoryReportSchema>;

export const retentionSweepReportSchema = z.object({
    ranAt: z.string(),
    dryRun: z.boolean(),
    firmHoldActive: z.boolean(),
    activeHoldCount: z.number().int(),
    categories: z.array(retentionCategoryReportSchema),
});
export type RetentionSweepReportDto = z.infer<typeof retentionSweepReportSchema>;

export const retentionOverviewSchema = z.object({
    schedule: z.array(retentionScheduleEntrySchema),
    holds: z.array(legalHoldSchema),
});
export type RetentionOverviewDto = z.infer<typeof retentionOverviewSchema>;

export const setRetentionCategorySchema = z.object({
    category: retentionCategorySchema,
    enabled: z.boolean(),
    /**
     * For a regulated category with `enabled: true` this is the ratification
     * record — REQUIRED there (who at Dowgate compliance ratified, and when).
     */
    note: z.string().nullable().optional(),
});
export type SetRetentionCategoryDto = z.infer<typeof setRetentionCategorySchema>;

export const createLegalHoldSchema = z.object({
    subjectKind: z.enum(['firm', 'client', 'user']),
    /** Client id / staff user id; omitted for a firm-wide hold. */
    subjectId: z.string().nullable().optional(),
    reason: z.string().min(1),
    reference: z.string().nullable().optional(),
});
export type CreateLegalHoldDto = z.infer<typeof createLegalHoldSchema>;

export const releaseLegalHoldSchema = z.object({
    id: z.string(),
    note: z.string().nullable().optional(),
});
export type ReleaseLegalHoldDto = z.infer<typeof releaseLegalHoldSchema>;

export const retentionContract = c.router(
    {
        getOverview: {
            method: 'GET',
            path: '/admin/retention',
            responses: { 200: retentionOverviewSchema },
            summary:
                'Read the retention schedule, per-category governance state, and the legal-hold register.',
        },
        preview: {
            method: 'POST',
            path: '/admin/retention/preview',
            body: z.object({}),
            responses: { 200: retentionSweepReportSchema },
            summary:
                'DRY-RUN the retention sweep: report what would be deleted without deleting anything.',
        },
        setCategory: {
            method: 'POST',
            path: '/admin/retention/category',
            body: setRetentionCategorySchema,
            responses: { 200: retentionScheduleEntrySchema, 400: errorBody },
            summary:
                'Record a per-category enablement decision. Enabling a regulated category requires a ratification note; audited.',
        },
        createHold: {
            method: 'POST',
            path: '/admin/retention/holds',
            body: createLegalHoldSchema,
            responses: { 200: legalHoldSchema, 400: errorBody },
            summary:
                'Place a legal hold (litigation / FCA enquiry) suspending retention deletion for its subject; audited.',
        },
        releaseHold: {
            method: 'POST',
            path: '/admin/retention/holds/release',
            body: releaseLegalHoldSchema,
            responses: { 200: legalHoldSchema, 404: errorBody },
            summary:
                'Release a legal hold (an update, never a delete — the register is a compliance record); audited.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
